import DOMPurify from 'dompurify';

/**
 * Clean HTML pasted into a rich-text field (e.g. the invoice description).
 *
 * Goal: preserve the *rendered result* of the paste — paragraph breaks, blank
 * lines, bold/italic/underline, alignment, color, size — while removing only
 * the non-rendering cruft a browser already ignores, especially Microsoft Word:
 *
 *   - `<o:*>` / `<w:*>` / `<m:*>` / `<v:*>` Office namespace tags (e.g. `<o:p>`).
 *     These are dropped but their inner content is kept (KEEP_CONTENT), so an
 *     empty `<o:p>&nbsp;</o:p>` still leaves the blank line it produced.
 *   - `mso-*` properties inside `style`, plus Word noise like point margins and
 *     stray `font-family` / `line-height` (only an allow-list of real
 *     formatting properties survives — see {@link ALLOWED_STYLE_PROPS}).
 *   - `class="Mso…"` and every other class (class is not in the attr allow-list).
 *   - HTML comments, including Word conditional comments (DOMPurify drops these).
 *   - Empty inline wrappers such as `<span></span>` left behind after cleaning.
 *
 * Note: only formatting *tags* and an allow-list of inline CSS survive. Word
 * indentation expressed as `margin-left` is intentionally dropped (it is mostly
 * noise and indistinguishable from Word's junk margins); indentation typed as
 * literal spaces/tabs is preserved by the field's `white-space: pre-wrap`.
 */

/** Standard formatting tags we keep from a rich paste. */
const ALLOWED_TAGS = [
    'b', 'strong', 'i', 'em', 'u', 's', 'strike', 'sub', 'sup',
    'p', 'div', 'br', 'span',
    'ul', 'ol', 'li',
    'a',
    'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
    'blockquote',
];

/** Attributes allowed to survive. `style` is further filtered below. */
const ALLOWED_ATTR = ['href', 'style', 'title', 'target', 'rel'];

/** Inline CSS properties that represent real, intended formatting. */
const ALLOWED_STYLE_PROPS = new Set([
    'font-weight', 'font-style',
    'text-decoration', 'text-decoration-line',
    'text-align', 'color', 'font-size',
]);

/** Inline tags that carry no meaning once they have no text/visual content. */
const EMPTY_INLINE_TAGS = ['span', 'b', 'strong', 'i', 'em', 'u', 's', 'strike', 'sub', 'sup', 'a'];

/**
 * Keep only allow-listed CSS declarations; drop Word noise (`mso-*`, margins,
 * `font-family`, `line-height`, …). Returns the rebuilt style string (empty if
 * nothing survived).
 */
function filterStyle(style: string): string {
    return style
        .split(';')
        .map(decl => {
            const colon = decl.indexOf(':');
            if (colon < 0) return null;
            const prop = decl.slice(0, colon).trim().toLowerCase();
            const value = decl.slice(colon + 1).trim();
            return {prop, value};
        })
        .filter((d): d is {prop: string; value: string} =>
            !!d && !!d.value && ALLOWED_STYLE_PROPS.has(d.prop) && !d.value.toLowerCase().includes('mso'))
        .map(({prop, value}) => `${prop}: ${value}`)
        .join('; ');
}

/**
 * Sanitize HTML from the clipboard for insertion into a rich-text contenteditable.
 * See the module doc comment for the full cleaning contract.
 */
export function sanitizeRichPaste(html: string): string {
    if (!html) return '';

    // Whitelist tags + attrs, keep the inner content of dropped tags (so
    // `<o:p>&nbsp;</o:p>` leaves its `&nbsp;`), and strip comments.
    const frag = DOMPurify.sanitize(html, {
        ALLOWED_TAGS,
        ALLOWED_ATTR,
        KEEP_CONTENT: true,
        RETURN_DOM_FRAGMENT: true,
    }) as unknown as DocumentFragment;

    // Filter surviving inline styles down to the formatting allow-list.
    frag.querySelectorAll('[style]').forEach(el => {
        const cleaned = filterStyle(el.getAttribute('style') || '');
        if (cleaned) el.setAttribute('style', cleaned);
        else el.removeAttribute('style');
    });

    // Drop empty inline wrappers (e.g. `<span></span>`) — but keep empty block
    // elements (`<p>`, `<div>`), which are intentional blank lines.
    EMPTY_INLINE_TAGS.forEach(tag => {
        frag.querySelectorAll(tag).forEach(el => {
            if (!el.textContent?.trim() && !el.querySelector('img, br')) el.remove();
        });
    });

    const container = document.createElement('div');
    container.appendChild(frag);
    return container.innerHTML;
}

export default sanitizeRichPaste;
